import { useAuthStore } from '../stores/auth-store.js';

import { detectSurface, overrideHeaderValue } from './subdomain.js';

const SUBDOMAIN_HEADER = 'X-Sitewise-Subdomain-Override';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** When true, do not attempt to refresh the token on 401. */
  skipRefresh?: boolean;
  /** Override the auth surface for the request (e.g. handoff exchange). */
  surfaceOverride?: 'org' | 'admin';
  /** Phase 10 — `Idempotency-Key` for safely retried offline mutations. */
  idempotencyKey?: string;
}

let refreshInflight: Promise<void> | null = null;

/**
 * Single-flight refresh: many concurrent requests that hit a 401 would
 * otherwise stampede the refresh endpoint. The first 401 starts the
 * refresh; everyone else awaits it and retries with the new token.
 */
async function refreshTokens(): Promise<void> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    const { refreshToken, kind, setTokens, clear } = useAuthStore.getState();
    if (kind === 'vendor' || !refreshToken || !kind) {
      clear();
      throw new ApiError(401, 'NO_REFRESH_TOKEN', 'No refresh token available');
    }
    const path = kind === 'admin' ? '/api/v1/admin/auth/refresh' : '/api/v1/auth/refresh';
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const surfaceHeader = overrideHeaderValue(detectSurface());
    if (surfaceHeader) (headers as Record<string, string>)[SUBDOMAIN_HEADER] = surfaceHeader;
    const res = await fetch(path, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      clear();
      throw new ApiError(res.status, 'REFRESH_FAILED', 'Refresh failed');
    }
    const data = (await res.json()) as { accessToken: string; refreshToken: string };
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken, kind });
  })().finally(() => {
    refreshInflight = null;
  });
  return refreshInflight;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, skipRefresh, surfaceOverride, idempotencyKey, headers: rawHeaders, ...rest } = opts;
  const headers = new Headers(rawHeaders);
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const surface = surfaceOverride
    ? { kind: surfaceOverride } as ReturnType<typeof detectSurface>
    : detectSurface();
  const surfaceHeader = overrideHeaderValue(surface);
  if (surfaceHeader) headers.set(SUBDOMAIN_HEADER, surfaceHeader);

  const { accessToken } = useAuthStore.getState();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const init: RequestInit = {
    ...rest,
    headers,
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
  };

  const url = path.startsWith('http') ? path : path.startsWith('/api') ? path : `/api/v1${path}`;
  let res = await fetch(url, init);

  const { kind } = useAuthStore.getState();
  if (res.status === 401 && !skipRefresh && kind !== 'vendor') {
    try {
      await refreshTokens();
      // Re-attach the new token and retry once.
      const { accessToken: newAccess } = useAuthStore.getState();
      if (newAccess) headers.set('Authorization', `Bearer ${newAccess}`);
      res = await fetch(url, { ...init, headers });
    } catch {
      // refresh failed — fall through to the original 401 response below
    }
  }

  if (!res.ok) {
    // The API serializes errors flat: `{ code, message, details? }`. The
    // earlier nested-shape assumption silently swallowed the `code` and
    // surfaced "HTTP 401" to the UI, breaking flows like the admin
    // login's `TOTP_REQUIRED` branch detection.
    let payload: { code?: string; message?: string; details?: unknown } | null = null;
    try {
      payload = await res.json();
    } catch {
      // ignore non-json error bodies
    }
    const code = payload?.code ?? 'HTTP_ERROR';
    const message = payload?.message ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message, payload?.details);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}
